import { Alert, AlertTitle, Badge, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import dcmjs from 'dcmjs'
import GroupedFilesBlock, { GroupedFilesBlockHandle } from '@features/subject/components/GroupedFilesBlock'
import SeriesRow from '@features/subject/components/SeriesRow'
import NonDICOMRow from '@features/subject/components/NonDICOMRow'
import DicomTagsDialog from '@features/subject/components/DicomTagsDialog'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { Meta, UppyFile } from '@uppy/core'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useCheckTagsValidity } from '@features/subject/hooks/useCheckTagsValidity.ts'
import CriteriaCheckDialog from '@features/subject/components/CriteriaCheckDialog'
import { useGetStudyById } from '@study_setup/hooks/useStudyQueries'

import {
  AdherenceCheckUpsertPayload,
  CheckItem,
  CheckStepProps,
  ContainerParam,
  ContainerType,
  DicomDataSet,
  NonDicomFile,
  Study,
  TagsProps,
} from '@features/subject/subject.types'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'

export const EXTENSION_TYPE_MAP: Record<string, string> = {
  pdf: 'PDF',
  jpg: 'JPEG_JPG',
  jpeg: 'JPEG_JPG',
  png: 'PNG',
  // bmp: 'BMP',
  // tiff: 'TIFF',
  // nifti: 'NIFTI',
  // mov: 'VIDEO_MOV',
  mp4: 'VIDEO_MP4',
  // doc: 'DOC_DOCX',
  // docx: 'DOC_DOCX',
  // Data / text types
  csv: 'CSV',
  txt: 'TXT',
  xml: 'XML',
  mat: 'MAT',
  r: 'R',
  // Medical signal / waveform types
  ecg: 'ECG',
  edf: 'EDF',
  edt: 'EDT',
  hea: 'HEA',
  dat: 'DAT',
  mrq: 'MRQ',
  mwf: 'MWF',
  rdt: 'RDT',
  scp: 'SCP',
  amps: 'AMPS',
}

const getFileNameWithoutExtension = (filename?: string): string => {
  if (!filename) {
    return ''
  }

  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1) {
    // No extension found, return the whole name
    return filename
  }

  // Return the name without the extension
  return filename.substring(0, lastDotIndex)
}

const getFileTypeAllowed = (data: Array<ContainerParam>, type: ContainerType): string[] => {
  const container = data.find((item) => item.type === type)
  return container ? container.fileTypesAllowed : []
}

const getModalityAllowed = (data: Array<ContainerParam>, type: ContainerType): string[] => {
  const container = data.find((item) => item.type === type)
  return container ? container.modalityAllowed : []
}

const checkFileTypeError = (files: NonDicomFile[], containerParams: ContainerParam[]): boolean => {
  return files.some((file) => {
    const fileExtension = file.name?.split('.').pop()?.toLowerCase()
    const mappedType = EXTENSION_TYPE_MAP[fileExtension || ''] || ''
    // Find a container that allows this file type
    const container = containerParams.find((x) => x.type === 'NON_DICOM' && x.fileTypesAllowed.includes(mappedType))
    // If no container found, it's an error
    return !container
  })
}

const checkModalityError = (allowedModalities: string[], files: Study[] | Study): boolean => {
  if (Array.isArray(files)) {
    // Handle the case where files is an array of Study objects
    return files.some((file) => {
      return file.series.some((series) => {
        return !allowedModalities.includes(series.modality)
      })
    })
  } else {
    // Handle the case where files is a single Study object
    return files.series.some((series) => {
      return !allowedModalities.includes(series.modality)
    })
  }
}

const checkModalityMissing = (
  containerParam: Array<ContainerParam>,
  dicomData: Study[],
  nonDicomData: NonDicomFile[],
): { DICOM: boolean; NON_DICOM: boolean } => {
  return {
    DICOM:
      dicomData.length > 0 && containerParam.some((item) => item.type === 'DICOM' && item.noFilesSubmitted === true),
    NON_DICOM:
      nonDicomData.length > 0 &&
      containerParam.some((item) => item.type === 'NON_DICOM' && item.noFilesSubmitted === true),
  }
}

export type CheckStepHandle = {
  validateAdherenceComments: () => Promise<boolean>
}

const CheckStep = forwardRef<CheckStepHandle, CheckStepProps & { onDataReady: () => void; parentIsLoading: boolean }>(
  function CheckStep(
    {
      uppy,
      containerParam,
      onCheckStepComplete,
      selectedSubmission,
      subjectPseudoId,
      setActiveStep,
      onDataReady,
      parentIsLoading,
      onAdherenceChecksReady,
    },
    ref,
  ) {
    const files = uppy.getFiles()
    const [dicomData, setDicomData] = useState<Study[]>([])
    const [nonDicomData, setNonDicomData] = useState<NonDicomFile[]>([])
    const commentBlockRefs = useRef<Map<string, GroupedFilesBlockHandle>>(new Map())
    const { studyId, timepointSubmissionId } = useParams<{
      studyId: string
      subjectId: string
      timepointSubmissionId: string
    }>()
    const { user } = useExtendedAuth()
    const { data: studyDetails } = useGetStudyById(studyId)
    console.log({ user })

    useImperativeHandle(ref, () => ({
      validateAdherenceComments: async () => {
        const results = await Promise.all(
          Array.from(commentBlockRefs.current.values()).map((block) => block.validateComment(true)),
        )
        return results.every(Boolean)
      },
    }))

    const [dicomTagsDialogState, setDicomTagsDialogState] = useState({
      open: false,
      seriesId: 0,
    })
    const [adherenceErrorsAlertVisible, setAdherenceErrorsAlertVisible] = useState(true)

    const [checkCriteriaDialogState, setCheckCriteriaDialogState] = useState<{
      open: boolean
      checks: CheckItem[]
    }>({ open: false, checks: [] })
    const { result: checkAdherenceResult, isLoading: isAdherenceCheckLoading } = useCheckTagsValidity(
      studyId!,
      dicomData,
    )

    // Internal state to track if CheckStep's data is fully processed and ready
    const [isCheckStepInternalDataReady, setIsCheckStepInternalDataReady] = useState(false)

    const nonDicomNonConvert = nonDicomData.filter((x) => !x.convert)
    const nonDicomConvert = nonDicomData.filter((x) => x.convert)

    const handleCriteriaDialogOpen = (seriesId: number, studyId: string) => {
      setCheckCriteriaDialogState({
        open: true,
        checks: checkAdherenceResult?.[studyId]?.series[seriesId].checks || [],
      })
    }

    const handleDicomTagsDialogOpen = (seriesId: number) => {
      setDicomTagsDialogState({ open: true, seriesId })
    }

    const handleDicomTagsDialogClose = () => {
      setDicomTagsDialogState({ ...dicomTagsDialogState, open: false })
    }

    const getTagsData = (dataSet: DicomDataSet): TagsProps[] => {
      const tags: TagsProps[] = []
      // Use nameMap so standard keyword → tag IDs are not overwritten by private tags
      // that reuse the same name in the full dictionary (e.g. PatientName).
      const nameMap = dcmjs.data.DicomMetaDictionary.nameMap as Record<string, { tag?: string }>

      for (const [key, value] of Object.entries(dataSet)) {
        tags.push({
          tagId: nameMap[key]?.tag || 'Unknown Tag',
          tagName: key,
          value: String(value),
        })
      }

      return tags
    }

    const setCommentForFailedStudy = (studyId: string, comment: string) => {
      const files = uppy.getFiles()

      files.forEach((file) => {
        if (file.meta.study_uid === studyId) {
          uppy.setFileMeta(file.id, { ...file.meta, comment })
        }
      })
    }

    const groupFilesByStudyId = async (files: UppyFile<Meta, Record<string, never>>[]) => {
      const dicomFiles: Record<string, Study> = {}
      const nonDicomFiles: NonDicomFile[] = []

      for (const file of files) {
        try {
          const arrayBuffer = await file.data.arrayBuffer()
          const byteArray = new Uint8Array(arrayBuffer)
          const isDicom = String.fromCharCode(...byteArray.slice(128, 132)) === 'DICM'

          if (!isDicom) {
            const container = containerParam.find((x) =>
              x.containerFileTypes.includes(EXTENSION_TYPE_MAP[file.extension]),
            )

            nonDicomFiles.push({
              id: file.id,
              basename: getFileNameWithoutExtension(file.name),
              name: file.name,
              acquisitionDate: file.meta.acquisitionDate as string,
              convert: container?.convertToDicom || false,
            })

            uppy.setFileMeta(file.id, {
              ...file.meta,
              modality: container?.convertToDicom ? 'OT' : undefined,
              container_submission_id: container?.containerSubmissionId,
            })
            continue
          }

          const dicomData = dcmjs.data.DicomMessage.readFile(arrayBuffer)
          const dataSet = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict)

          const studyId = dataSet.StudyInstanceUID || 'Unknown Study'
          const studyDescription = dataSet.StudyDescription || 'Unknown Study'
          const seriesInstanceUID = dataSet.SeriesInstanceUID || 'Unknown SeriesInstanceUID'
          const sopInstanceUID = dataSet.SOPInstanceUID || null
          const modality = dataSet.Modality || 'Unknown Modality'
          const seriesId = dataSet.SeriesNumber || 'Unknown SeriesNumber'
          const description = dataSet.SeriesDescription || 'Unknown Description'
          const acquisitionDate = dataSet.AcquisitionDate || null
          const studyDate = dataSet.StudyDate || null

          if (!dicomFiles[studyId]) {
            dicomFiles[studyId] = {
              name: studyId,
              studyDescription,
              date: studyDate ? dayjs(studyDate, 'YYYYMMDD').format('DD/MM/YYYY') : '-',
              series: [],
            }
          }

          const series = dicomFiles[studyId].series.find((s) => s.seriesInstanceUID === seriesInstanceUID)

          if (!series) {
            dicomFiles[studyId].series.push({
              fileIds: [file.id],
              tagData: getTagsData(dataSet),
              description,
              tooltipText: description || 'Series',
              acquisitionDate: acquisitionDate ? dayjs(acquisitionDate, 'YYYYMMDD').format('DD/MM/YYYY') : '-',
              seriesNumber: seriesId,
              instances: [sopInstanceUID],
              modality,
              seriesInstanceUID,
            })

            uppy.setFileMeta(file.id, {
              ...file.meta,
              study_uid: studyId,
              studyDescription,
              seriesInstanceUID,
              totalInstances: 1,
              seriesDescription: description,
              container_submission_id: containerParam.find((x) => x.modality === modality)?.containerSubmissionId,
              modality,
              seriesId,
              acquisitionDate,
            })
          } else {
            series.fileIds.push(file.id)

            if (series.instances.some((instance) => instance === sopInstanceUID)) {
              // This feature will be handled in future
            } else {
              series.instances.push(sopInstanceUID)

              uppy.setFileMeta(file.id, {
                ...file.meta,
                study_uid: studyId,
                studyDescription,
                seriesInstanceUID,
                totalInstances: series.instances.length,
                seriesDescription: description,
                container_submission_id: containerParam.find((x) => x.modality === modality)?.containerSubmissionId,
                modality,
                seriesId,
                acquisitionDate,
              })
            }
          }
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error)
        }
      }

      return { dicomFiles: Object.values(dicomFiles), nonDicomFiles }
    }

    const handleCloseCriteriaCheckDialog = () => {
      setCheckCriteriaDialogState({ open: false, checks: [] })
    }

    useEffect(() => {
      const fetchAndGroupFiles = async () => {
        setIsCheckStepInternalDataReady(false)
        const { dicomFiles, nonDicomFiles } = await groupFilesByStudyId(files)
        console.log('Grouped DICOM files:', dicomFiles)
        console.log('Grouped non-DICOM files:', nonDicomFiles)
        setDicomData(dicomFiles)
        setNonDicomData(nonDicomFiles)
      }
      if (containerParam.length > 0) {
        fetchAndGroupFiles()
      }
    }, [files.length, containerParam.length])

    useEffect(() => {
      // Check if all necessary data is loaded and adherence check is complete
      const allDataProcessed =
        (dicomData.length > 0 || nonDicomData.length > 0) && !isAdherenceCheckLoading && checkAdherenceResult
      const noFilesCase = files.length === 0

      if (allDataProcessed || noFilesCase) {
        setIsCheckStepInternalDataReady(true)
        onDataReady() // Notify parent that data is ready
      } else {
        setIsCheckStepInternalDataReady(false)
      }
    }, [dicomData, nonDicomData, isAdherenceCheckLoading, checkAdherenceResult, files.length, onDataReady])

    // Publish CheckStep results via React state (primary) and Uppy meta (backup) for upload PATCH
    useEffect(() => {
      if (!checkAdherenceResult) return

      const checksByContainer: Record<string, AdherenceCheckUpsertPayload[]> = {}

      const mergeIntoContainer = (containerId: string, adherenceChecks: AdherenceCheckUpsertPayload[]) => {
        const map = new Map<string, AdherenceCheckUpsertPayload>()
        ;(checksByContainer[containerId] || []).forEach((check) => {
          map.set(`${check.rule_id}:${check.series_number}:${check.tag_key}`, check)
        })
        adherenceChecks.forEach((check) => {
          map.set(`${check.rule_id}:${check.series_number}:${check.tag_key}`, check)
        })
        checksByContainer[containerId] = Array.from(map.values())
      }

      Object.entries(checkAdherenceResult).forEach(([studyName, studyResult]) => {
        Object.entries(studyResult.series).forEach(([seriesNumber, seriesResult]) => {
          const adherenceChecks: AdherenceCheckUpsertPayload[] = seriesResult.checks
            .filter((check) => !!check.ruleId)
            .map((check) => ({
              rule_id: check.ruleId as string,
              series_number: String(seriesNumber),
              tag_key: check.tagId,
              tag_value: check.actualValue === 'N/A' ? null : check.actualValue,
              is_adherent: check.status === 'passed',
            }))

          if (!adherenceChecks.length) return

          // Scope to this series' files only (grouped by SeriesInstanceUID in dicomData).
          // Do NOT match all Uppy files by study_uid + seriesNumber — that spreads one
          // series' checks onto every container that shares the same SeriesNumber (e.g. MR+CT "1").
          const study = dicomData.find((item) => item.name === studyName)
          const series = study?.series.find((item) => String(item.seriesNumber) === String(seriesNumber))
          const seriesFiles = (series?.fileIds || []).map((fileId) => uppy.getFile(fileId)).filter(Boolean)

          const containerId =
            seriesFiles.map((file) => file?.meta.container_submission_id as string | undefined).find(Boolean) ||
            containerParam.find((container) => container.modality === series?.modality)?.containerSubmissionId

          if (containerId) {
            mergeIntoContainer(containerId, adherenceChecks)
          }

          seriesFiles.forEach((file) => {
            if (file) {
              uppy.setFileMeta(file.id, { ...file.meta, adherence_checks: adherenceChecks })
            }
          })
        })
      })

      onAdherenceChecksReady?.(checksByContainer)
    }, [checkAdherenceResult, containerParam, dicomData, onAdherenceChecksReady, uppy])

    const renderStudyActions = (studyItem: Study) => {
      return (
        <Box display="flex" gap={1}>
          <IconButton onClick={() => handleDeleteStudy(studyItem)}>
            <DeleteOutlineOutlinedIcon sx={{ color: 'text.primary' }} />
          </IconButton>
        </Box>
      )
    }

    const handleDeleteStudy = (studyItem: Study) => {
      const fileIdsToDelete = studyItem.series.flatMap((series) => series.fileIds)

      fileIdsToDelete.forEach((fileId) => {
        uppy.removeFile(fileId)
      })

      setDicomData(dicomData.filter((study) => study.name !== studyItem.name))

      if (dicomData.length <= 1) {
        setActiveStep(0)
      }
    }

    const renderNonDicomActions = () => {
      return (
        <IconButton onClick={handleDeleteAllNonDicom}>
          <DeleteOutlineOutlinedIcon sx={{ color: 'text.primary' }} />
        </IconButton>
      )
    }

    const handleDeleteAllNonDicom = () => {
      const fileIdsToDelete = nonDicomData.map((file) => file.id)
      fileIdsToDelete.forEach((fileId) => {
        uppy.removeFile(fileId)
      })

      setNonDicomData([])
      setActiveStep(0)
    }

    const onViewAlert = (): void => {
      const errorElement = document.getElementById('error')

      if (errorElement)
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
    }

    const onDeleteDicomFiles = (seriesNumber: number, filesId: string[]) => {
      const newDicomFiles: Study[] = dicomData
        .map((study) => {
          const filteredSeries = study.series.filter((series) => series.seriesNumber !== seriesNumber)

          // If the series array is empty after filtering, return null to remove the study
          if (filteredSeries.length === 0) {
            return null
          }

          // Otherwise, return the updated study with the filtered series
          return {
            ...study,
            series: filteredSeries,
          }
        })
        .filter((study): study is Study => study !== null) // Remove null values (empty studies)

      setDicomData(newDicomFiles)

      // Remove files from uppy
      filesId.forEach((fileId) => {
        uppy.removeFile(fileId)
      })
      const remainingFiles = uppy.getFiles()
      if (remainingFiles.length === 0 || newDicomFiles.length === 0) {
        setActiveStep(0)
      }
    }

    const onUpdateModality = (seriesNumber: number, fileIds: string[], newModality: string) => {
      fileIds.forEach((fileId) => {
        // Find the file in Uppy's current files
        const fileToUpdate = uppy.getFile(fileId)

        if (fileToUpdate) {
          // Update the modality in the file's meta-data
          uppy.setFileMeta(fileToUpdate.id, {
            ...fileToUpdate.meta,
            modality: newModality,
            container_submission_id: containerParam.find((x) => x.modality === newModality)?.containerSubmissionId,
          })
        }
      })
      setDicomData((prevDicomData) => {
        return prevDicomData.map((study) => {
          return {
            ...study,
            series: study.series.map((series) => {
              if (series.seriesNumber === seriesNumber) {
                return {
                  ...series,
                  modality: newModality,
                }
              }
              return series
            }),
          }
        })
      })
    }

    const onDeleteNonDicomFiles = (filesId: string[]) => {
      // Filter out non-DICOM files whose IDs are in filesId array
      const newNonDicomFiles: NonDicomFile[] = nonDicomData.filter((file) => !filesId.includes(file.id))
      setNonDicomData(newNonDicomFiles)

      // Remove files from uppy
      filesId.forEach((fileId) => {
        uppy.removeFile(fileId)
      })

      const remainingFiles = uppy.getFiles()
      if (remainingFiles.length === 0 || newNonDicomFiles.length === 0) {
        setActiveStep(0)
      }
    }

    const fileTypeAllowedNonDicom = getFileTypeAllowed(containerParam, 'NON_DICOM')
    const modalityAllowed = getModalityAllowed(containerParam, 'DICOM')

    const showFileTypeError = checkFileTypeError(nonDicomData, containerParam)
    const showModalityError = checkModalityError(modalityAllowed, dicomData)
    const showModalityMissing = checkModalityMissing(containerParam, dicomData, nonDicomData)

    useEffect(() => {
      const shouldContinue = !showModalityError && !showFileTypeError
      onCheckStepComplete(shouldContinue)

      return () => {
        onCheckStepComplete(false)
      }
    }, [showModalityError, showFileTypeError, onCheckStepComplete])

    if (parentIsLoading) {
      return null
    }

    // If parent is not loading, then CheckStep can manage its own internal loading state
    if (!isCheckStepInternalDataReady) {
      return (
        <Box pb={12}>
          <Typography color="text.primary" variant="h4">
            Exams and adherence criteria check
          </Typography>
          <Typography color="text.secondary" variant="body2" mb={4}>
            Check the exams from uploaded files and enter details for non-DICOM files.
          </Typography>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="50vh">
            <CircularProgress size={40} />
            <Typography color="text.secondary" variant="body1" mt={2}>
              Loading adherence criteria...
            </Typography>
          </Box>
        </Box>
      )
    }

    return (
      <Box pb={12}>
        <Typography color="text.primary" variant="h4">
          Exams and adherence criteria check
        </Typography>
        <Typography color="text.secondary" variant="body2" mb={4}>
          Check the exams from uploaded files and enter details for non-DICOM files.
        </Typography>
        {checkAdherenceResult &&
        Object.values(checkAdherenceResult).some((x) => !x.isStudyValid) &&
        adherenceErrorsAlertVisible ? (
          <Alert severity="error" variant="filled" sx={{ mb: 4 }} onClose={() => setAdherenceErrorsAlertVisible(false)}>
            <AlertTitle>We identified exam criteria issues during the adherence check</AlertTitle>
            Add a comment explaining the reason for the issues to submit the file upload
          </Alert>
        ) : null}

        {(showFileTypeError || showModalityError) && (
          <Alert
            sx={{ mb: 2 }}
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onViewAlert}>
                Bring me there
              </Button>
            }
          >
            <Typography variant="body1" fontWeight="700" component={'p'}>
              You uploaded a file with an unexpected modality or file type
            </Typography>
            <Typography variant="body1" component={'p'}>
              Please remove it and add the correct file to complete the upload.
            </Typography>
          </Alert>
        )}
        {showModalityMissing.DICOM ||
          (showModalityMissing.NON_DICOM && (
            <Alert
              sx={{ mb: 2 }}
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={onViewAlert}>
                  Bring me there
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="700" component={'p'}>
                You uploaded a file with a modality previously marked as missing
              </Typography>
              <Typography variant="body1" component={'p'}>
                Please remove it and add the correct file to complete the upload.
              </Typography>
            </Alert>
          ))}
        <Stack gap={2}>
          {dicomData?.map((item, index) => {
            const showError = checkModalityError(modalityAllowed, item)
            const isFailed = !checkAdherenceResult?.[item.name]?.isStudyValid
            return (
              <GroupedFilesBlock
                key={index}
                ref={
                  isFailed
                    ? (instance) => {
                        if (instance) {
                          commentBlockRefs.current.set(item.name, instance)
                        } else {
                          commentBlockRefs.current.delete(item.name)
                        }
                      }
                    : undefined
                }
                title={item.studyDescription}
                subtitle={`Exam date: ${item.date}`}
                onCommentAdd={isFailed ? (comment) => setCommentForFailedStudy(item.name, comment) : undefined}
                actions={renderStudyActions(item)}
                status={isFailed ? 'FAILED' : 'PASSED'}
                showModalityError={showError}
                showModalityMissing={showModalityMissing.DICOM}
              >
                <Box pt={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" mr={2.25}>
                      Series
                    </Typography>
                    <Badge variant="standard" color="secondary" badgeContent={item.series.length} />
                  </Box>
                  <Box borderRadius={3} overflow="hidden">
                    {item.series.map((series, subIndex) => (
                      <SeriesRow
                        key={subIndex}
                        fileIds={series.fileIds}
                        isBorderNeeded={subIndex !== item.series.length - 1}
                        description={series.description}
                        acquisitionDate={series.acquisitionDate}
                        seriesNumber={series.seriesNumber}
                        instances={series.instances.length}
                        modality={series.modality}
                        tooltipText={series.tooltipText}
                        modalityAllowed={modalityAllowed}
                        onDicomTagsDialogOpen={() => handleDicomTagsDialogOpen(series.seriesNumber)}
                        status={
                          checkAdherenceResult?.[item.name]?.series[series.seriesNumber]?.isSeriesValid
                            ? 'PASSED'
                            : 'FAILED'
                        }
                        onCriteriaDialogOpen={() => handleCriteriaDialogOpen(series.seriesNumber, item.name)}
                        onDelete={onDeleteDicomFiles}
                        onUpdateModality={onUpdateModality}
                      />
                    ))}
                  </Box>
                </Box>
              </GroupedFilesBlock>
            )
          })}

          {nonDicomData.length > 0 && (
            <GroupedFilesBlock
              title="Non-DICOM files"
              subtitle="Complete the missing information for each file. Adherence criteria check is not required."
              actions={renderNonDicomActions()}
              showFileTypeError={showFileTypeError}
              showModalityMissing={showModalityMissing.NON_DICOM}
            >
              {nonDicomConvert.length > 0 && (
                <Box pt={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" mr={2.25}>
                      Files to be converted to DICOM
                    </Typography>
                    <Badge variant="standard" color="secondary" badgeContent={nonDicomConvert.length} />
                  </Box>
                  <Box borderRadius={3} overflow="hidden">
                    {nonDicomConvert.map((item, index) => (
                      <NonDICOMRow
                        uppy={uppy}
                        fileId={item.id}
                        key={index}
                        name={item.name}
                        isBorderNeeded={index !== nonDicomData.length - 1}
                        seriesNumber={index + 1}
                        tooltipText={item.name}
                        seriesDescriptionText={item.basename}
                        fileTypeAllowed={fileTypeAllowedNonDicom}
                        onDelete={onDeleteNonDicomFiles}
                        mode="CONVERT"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {nonDicomNonConvert.length > 0 && (
                <Box pt={4}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" mr={2.25}>
                      Files not to be converted to DICOM
                    </Typography>
                    <Badge variant="standard" color="secondary" badgeContent={nonDicomNonConvert.length} />
                  </Box>
                  <Box borderRadius={3} overflow="hidden">
                    {nonDicomNonConvert.map((item, index) => {
                      const fileExtension = item.name?.split('.').pop()?.toLowerCase()
                      const mappedType = EXTENSION_TYPE_MAP[fileExtension || ''] || ''
                      const container = containerParam.find(
                        (x) => x.type === 'NON_DICOM' && x.fileTypesAllowed.includes(mappedType),
                      )
                      return (
                        <NonDICOMRow
                          uppy={uppy}
                          fileId={item.id}
                          key={index}
                          name={item.name}
                          isBorderNeeded={index !== nonDicomData.length - 1}
                          seriesNumber={index + 1}
                          tooltipText={item.name}
                          seriesDescriptionText={item.basename}
                          fileTypeAllowed={container?.fileTypesAllowed || []}
                          onDelete={onDeleteNonDicomFiles}
                          mode="NON_CONVERT"
                        />
                      )
                    })}
                  </Box>
                </Box>
              )}
            </GroupedFilesBlock>
          )}
        </Stack>
        {dicomTagsDialogState.open && dicomData && studyId && (
          <DicomTagsDialog
            seriesId={dicomTagsDialogState.seriesId}
            studyId={studyId}
            dicomData={dicomData}
            containerParam={containerParam}
            onClose={handleDicomTagsDialogClose}
            dynamicValues={{
              study_id: studyDetails?.user_facing_study_id || studyId,
              site_id: subjectPseudoId && subjectPseudoId.includes('-') ? subjectPseudoId.split('-')[0] : '',
              subject_id: subjectPseudoId || '',
              timepoint_id: timepointSubmissionId || '',
              timepoint_name: selectedSubmission?.name || '',
            }}
          />
        )}

        {checkCriteriaDialogState.open && (
          <CriteriaCheckDialog checks={checkCriteriaDialogState.checks} onClose={handleCloseCriteriaCheckDialog} />
        )}
      </Box>
    )
  },
)

export default CheckStep
